import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Loader2, Send, Paperclip, Download, Trash2, FileText, Image as ImageIcon, Video as VideoIcon, Music, MessageSquare, Files } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_BADGE = {
  client:    { bg: 'bg-blue-500',    label: 'Client',     emoji: '👤' },
  expert:    { bg: 'bg-amber-500',   label: 'Expert',     emoji: '🛡️' },
  admin:     { bg: 'bg-amber-500',   label: 'Admin',      emoji: '🛡️' },
  analyst:   { bg: 'bg-cyan-500',    label: 'Analyste',   emoji: '🔍' },
  chiffreur: { bg: 'bg-emerald-500', label: 'Chiffreur',  emoji: '💰' },
  partner:   { bg: 'bg-purple-500',  label: 'Partenaire', emoji: '🤝' },
};

const formatBytes = (b) => (b < 1024 ? `${b}o` : b < 1048576 ? `${(b / 1024).toFixed(1)} Ko` : `${(b / 1048576).toFixed(1)} Mo`);
const formatDate = (s) => {
  try {
    return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return s;
  }
};

/**
 * ProjectCommunication — Reusable component for messaging + attachments
 * scoped to ONE project_id. Used in client, expert, analyst, chiffreur, partner dashboards.
 *
 * Props:
 *   - projectId: string (required)
 *   - token: string (required) — JWT bearer token
 *   - viewerRole: string (optional) — for UI hints, computed from API response otherwise
 *   - compact: bool — if true, smaller layout
 */
export const ProjectCommunication = ({ projectId, token, compact = false }) => {
  const [tab, setTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [viewerRole, setViewerRole] = useState(null);
  const [viewerId, setViewerId] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    if (!projectId || !token) return;
    try {
      const [msgRes, attRes] = await Promise.all([
        axios.get(`${API}/api/projects/${projectId}/messages`, { headers }),
        axios.get(`${API}/api/projects/${projectId}/attachments`, { headers }),
      ]);
      setMessages(msgRes.data.messages || []);
      setAttachments(attRes.data.attachments || []);
      setViewerRole(msgRes.data.viewer_role);
      setViewerId(msgRes.data.viewer_id);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur de chargement';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 15000); // polling every 15s
    return () => clearInterval(interval);
  }, [fetchAll]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const txt = text.trim();
    if (!txt) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/projects/${projectId}/messages`, { text: txt }, { headers });
      setMessages((prev) => [...prev, res.data]);
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Envoi échoué');
    } finally {
      setSending(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (100 Mo max)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/projects/${projectId}/attachments`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [res.data, ...prev]);
      toast.success(`${file.name} ajouté`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload échoué');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = (att) => {
    const url = `${API}/api/projects/${projectId}/attachments/${att.id}/download?auth=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Supprimer "${att.filename}" ?`)) return;
    try {
      await axios.delete(`${API}/api/projects/${projectId}/attachments/${att.id}`, { headers });
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success('Fichier supprimé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suppression refusée');
    }
  };

  const isMine = (senderId) => senderId === viewerId;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" data-testid="project-comm-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gold mr-2" />
        <span className="text-sm text-white/60">Chargement…</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-navy-700/50 border border-white/10 overflow-hidden" data-testid="project-comm-root">
      {/* Tabs */}
      <div className="flex border-b border-white/10 bg-navy-800/50">
        <button
          onClick={() => setTab('messages')}
          data-testid="comm-tab-messages"
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all ${
            tab === 'messages' ? 'text-gold bg-white/5 border-b-2 border-gold' : 'text-white/60 hover:text-white/90'
          }`}
        >
          <MessageSquare className="w-4 h-4" />
          Messagerie ({messages.length})
        </button>
        <button
          onClick={() => setTab('files')}
          data-testid="comm-tab-files"
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all ${
            tab === 'files' ? 'text-gold bg-white/5 border-b-2 border-gold' : 'text-white/60 hover:text-white/90'
          }`}
        >
          <Files className="w-4 h-4" />
          Documents ({attachments.length})
        </button>
      </div>

      {/* Messages tab */}
      {tab === 'messages' && (
        <div className="flex flex-col" data-testid="comm-messages-pane">
          <div className={`overflow-y-auto p-3 space-y-2 ${compact ? 'h-72' : 'h-96'}`}>
            {messages.length === 0 ? (
              <div className="text-center text-white/40 text-sm py-12">
                Aucun message. Soyez le premier à écrire.
              </div>
            ) : (
              messages.map((m) => {
                const role = ROLE_BADGE[m.sender_role] || ROLE_BADGE.client;
                const mine = isMine(m.sender_id);
                return (
                  <div
                    key={m.id}
                    className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${m.id}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-2.5 ${mine ? 'bg-gold/20 border border-gold/30' : 'bg-white/5 border border-white/10'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`${role.bg} text-white text-[10px] px-1.5 py-0.5 rounded font-semibold`}>
                          {role.emoji} {role.label}
                        </span>
                        <span className="text-white/50 text-xs">{m.sender_name}</span>
                        <span className="text-white/30 text-xs ml-auto">{formatDate(m.created_at)}</span>
                      </div>
                      <div className="text-white/90 text-sm whitespace-pre-wrap break-words">{m.text}</div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>
          {/* Input */}
          <div className="border-t border-white/10 p-2 bg-navy-800/30 flex gap-2">
            <textarea
              data-testid="comm-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Écrivez votre message… (Entrée = envoyer)"
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/40 resize-none focus:outline-none focus:border-gold/60"
              rows={2}
              disabled={sending}
            />
            <Button
              onClick={sendMessage}
              disabled={sending || !text.trim()}
              className="bg-gold hover:bg-gold/90 text-black self-end"
              data-testid="comm-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Files tab */}
      {tab === 'files' && (
        <div className="flex flex-col" data-testid="comm-files-pane">
          <div className={`overflow-y-auto p-3 space-y-2 ${compact ? 'h-72' : 'h-96'}`}>
            {attachments.length === 0 ? (
              <div className="text-center text-white/40 text-sm py-12">
                Aucun document. Uploadez le premier.
              </div>
            ) : (
              attachments.map((a) => {
                const role = ROLE_BADGE[a.uploaded_by_role] || ROLE_BADGE.client;
                const ct = a.content_type || '';
                const isImg = ct.startsWith('image/');
                const isVid = ct.startsWith('video/');
                const isAud = ct.startsWith('audio/');
                const Icon = isVid ? VideoIcon : isAud ? Music : isImg ? ImageIcon : FileText;
                const canDelete = a.uploaded_by_id === viewerId || ['admin', 'expert'].includes(viewerRole);
                return (
                  <div
                    key={a.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10 hover:border-gold/30 transition-all"
                    data-testid={`attachment-${a.id}`}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-gold/20 text-gold flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">{a.filename}</div>
                      <div className="text-xs text-white/50 flex items-center gap-2 flex-wrap">
                        <span className={`${role.bg} text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap`}>
                          {role.emoji} {role.label}
                        </span>
                        <span className="truncate max-w-[120px]">{a.uploaded_by_name}</span>
                        <span>·</span>
                        <span className="whitespace-nowrap">{formatBytes(a.size)}</span>
                        <span>·</span>
                        <span className="whitespace-nowrap">{formatDate(a.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => downloadAttachment(a)}
                        className="p-2 rounded hover:bg-white/10 text-white/70 hover:text-white"
                        title="Télécharger"
                        data-testid={`download-${a.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => deleteAttachment(a)}
                          className="p-2 rounded hover:bg-red-500/20 text-red-300 hover:text-red-100"
                          title="Supprimer"
                          data-testid={`delete-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {/* Upload */}
          <div className="border-t border-white/10 p-2 bg-navy-800/30">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp4,.mov,.avi,.webm,.mkv,.mpeg,.mp3,.wav,.m4a,.zip,application/pdf,image/*,video/*,audio/*"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              className="hidden"
              data-testid="comm-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full bg-gold hover:bg-gold/90 text-black"
              data-testid="comm-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
              {uploading ? 'Upload…' : 'Ajouter (vidéo, photo, PDF, doc — max 100 Mo)'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCommunication;
